/**
 * AdminAiRules — pannello Regole AI (base di conoscenza insegnabile).
 * Le regole guidano la ricerca AI del Preventivatore e l'Assistente.
 * CRUD completo: aggiungi, modifica inline, elimina.
 */
import React, { useState, useEffect, useCallback } from "react";
import { BrainCircuit, Plus, Pencil, Trash2, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function AdminAiRules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newText, setNewText] = useState("");
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/ai-rules");
      setRules(r.data || []);
    } catch { toast.error("Errore caricamento regole"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function addRule() {
    const text = newText.trim();
    if (text.length < 8) { toast.error("Descrivi la regola in una frase completa"); return; }
    setSaving(true);
    try {
      await api.post("/admin/ai-rules", { text });
      toast.success("Regola memorizzata");
      setNewText("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  }

  async function saveEdit(id) {
    const text = editText.trim();
    if (text.length < 8) { toast.error("Descrivi la regola in una frase completa"); return; }
    try {
      await api.put(`/admin/ai-rules/${id}`, { text });
      toast.success("Regola aggiornata");
      setEditId(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore aggiornamento"); }
  }

  async function removeRule(id) {
    if (!window.confirm("Eliminare questa regola? L'AI smetterà di applicarla.")) return;
    try {
      await api.delete(`/admin/ai-rules/${id}`);
      toast.success("Regola eliminata");
      load();
    } catch { toast.error("Errore eliminazione"); }
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="ai-rules-page">
      <div>
        <div className="text-[11px] tracking-[0.2em] uppercase text-slate1 flex items-center gap-1.5">
          <BrainCircuit size={12} /> Regole AI
        </div>
        <h2 className="font-display text-2xl text-ink mt-1">Conoscenza insegnata all'AI</h2>
        <div className="text-xs text-slate-500 mt-0.5 max-w-2xl">
          Queste regole guidano la ricerca AI del Preventivatore e le risposte dell'Assistente.
          Puoi insegnarle anche in chat ("ricordati che…"): compariranno qui.
        </div>
      </div>

      <div className="bg-white border border-ink/10 rounded-sm p-4">
        <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1.5">Nuova regola</label>
        <div className="flex gap-2 items-start">
          <textarea
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            placeholder='Es. "Una sedia ha la seduta mai più alta di 50 cm" · "Uno sgabello ha la seduta mai più bassa di 60 cm"'
            rows={2}
            className="flex-1 border border-ink/20 px-3 py-2 rounded-sm bg-cream text-sm resize-none"
            data-testid="ai-rule-new-input"
          />
          <button
            onClick={addRule}
            disabled={saving}
            className="bg-ink text-cream px-4 py-2.5 text-[11px] tracking-[0.2em] uppercase rounded-sm disabled:opacity-60 whitespace-nowrap"
            data-testid="ai-rule-add-btn"
          >
            {saving ? <Loader2 size={12} className="inline animate-spin" /> : <Plus size={12} className="inline mr-1" />} Aggiungi
          </button>
        </div>
      </div>

      <div className="bg-white border border-ink/10 rounded-sm">
        <div className="p-4 border-b border-ink/10 text-[11px] tracking-[0.2em] uppercase text-slate1">
          Regole attive ({rules.length})
        </div>
        {loading ? (
          <div className="p-8 text-center text-slate1"><Loader2 size={20} className="inline animate-spin" /></div>
        ) : rules.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="ai-rules-empty">
            Nessuna regola ancora. Aggiungine una qui o insegnala in chat all'assistente.
          </div>
        ) : (
          <div className="divide-y divide-ink/5">
            {rules.map((r) => (
              <div key={r.id} className="p-4 flex items-start gap-3" data-testid={`ai-rule-${r.id}`}>
                {editId === r.id ? (
                  <>
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      rows={2}
                      className="flex-1 border border-ink/20 px-3 py-2 rounded-sm bg-cream text-sm resize-none"
                      data-testid={`ai-rule-edit-input-${r.id}`}
                    />
                    <button onClick={() => saveEdit(r.id)} className="text-emerald-700 hover:text-ink mt-1" title="Salva" data-testid={`ai-rule-save-${r.id}`}><Check size={16} /></button>
                    <button onClick={() => setEditId(null)} className="text-slate1 hover:text-ink mt-1" title="Annulla"><X size={16} /></button>
                  </>
                ) : (
                  <>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-ink leading-relaxed">{r.text}</div>
                      <div className="text-[10px] text-slate-400 mt-1">
                        {r.created_by && <>insegnata da {r.created_by} · </>}
                        {r.created_at && new Date(r.created_at).toLocaleDateString("it-IT")}
                      </div>
                    </div>
                    <button
                      onClick={() => { setEditId(r.id); setEditText(r.text); }}
                      className="text-slate1 hover:text-ink mt-1" title="Modifica"
                      data-testid={`ai-rule-edit-${r.id}`}
                    ><Pencil size={15} /></button>
                    <button
                      onClick={() => removeRule(r.id)}
                      className="text-slate1 hover:text-destructive mt-1" title="Elimina"
                      data-testid={`ai-rule-del-${r.id}`}
                    ><Trash2 size={15} /></button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
